/**
 * Dashboard Registry — central list of all registered dashboard plugins.
 *
 * Each plugin implements DashboardPlugin and is added to this array.
 * The DashboardComponent renders the selected plugin from this registry.
 * With 2+ visible plugins, DashboardSelector shows a sidebar to switch
 * between them.
 *
 * Some entries are conditionally visible per user: DashboardComponent
 * computes the visible set via `visibleDashboardsFor` (weblab-tracker →
 * FINN team 104 members, weblabs-visual → SPB Finance FINN team members,
 * audit-console → superusers). Filtering there, rather than mutating this
 * static registry, keeps each gate a pure function of the user's context.
 */

import type { DashboardPlugin } from "./types";
import { spbAnalyticsDashboard } from "./plugins/spbAnalytics";
import { weblabTrackerDashboard } from "./plugins/weblabTracker";
import { weblabsVisualDashboard } from "./plugins/weblabs";
import { auditConsoleDashboard } from "./plugins/auditConsole";
import { publishedReportsDashboard } from "./plugins/publishedReports";
import { isSuperuser } from "../../audit/superuser";

export const dashboardRegistry: DashboardPlugin[] = [
  spbAnalyticsDashboard,
  weblabTrackerDashboard,
  weblabsVisualDashboard,
  auditConsoleDashboard,
  publishedReportsDashboard,
];

/** The per-user inputs deciding which registry entries are visible. */
export interface DashboardVisibilityContext {
  /** Whether the user is a member of the Weblab Tracker team (FINN team 104). */
  isWeblabTrackerMember?: boolean;
  /**
   * Whether the user is a member of the SPB Finance FINN team (team id 2).
   * Gates the `weblabs-visual` dashboard — the underlying weblab-science data
   * has known accuracy limitations, so the view is restricted to SPB Finance
   * internal use (leadership decision).
   */
  isSpbFinanceMember?: boolean;
  /** The current user's numeric id (superuser gate for audit-console). */
  userId?: number;
  /**
   * Hide the `spb-analytics` entry. Set by MainPage for VESL (Brief) teams
   * whose name carries no SP/SB/SPB token — the rule that used to hide the
   * whole Dashboard rail item for them. The rail is now shown to every team so
   * Published Reports is reachable (publish-report spec, Req 8); the SPB
   * Analytics content gate itself is unchanged.
   */
  hideSpbAnalytics?: boolean;
}

/**
 * Return the registry entries the given user may see. Pure, so the gates are
 * directly testable:
 * - `weblab-tracker` is visible only to members of FINN team 104 (the
 *   pre-existing membership gate, unchanged).
 * - `weblabs-visual` is visible only to members of the SPB Finance FINN team
 *   (team id 2). The weblab-science source data is directionally correct but
 *   not dollar-accurate, so the dashboard is restricted to SPB Finance
 *   internal use. Unresolved membership (undefined) hides it, as with
 *   weblab-tracker.
 * - `audit-console` is visible only to superusers — the frontend
 *   defense-in-depth mirror of the server-authoritative pipeline gate
 *   (audit-log spec Req 9.1/9.2, audit-usage-report Req 1.1/1.2). For
 *   everyone else the entry is entirely absent: no selector item, no
 *   deep-link target, no fallback selection.
 * - `spb-analytics` is hidden only when `hideSpbAnalytics` is set (VESL teams
 *   without an SP/SB/SPB token; see DashboardVisibilityContext).
 * - Every other entry — including `published-reports` — is visible to everyone.
 */
export function visibleDashboardsFor(
  registry: DashboardPlugin[],
  ctx: DashboardVisibilityContext
): DashboardPlugin[] {
  return registry.filter((d) => {
    if (d.id === "weblab-tracker") return ctx.isWeblabTrackerMember === true;
    if (d.id === "weblabs-visual") return ctx.isSpbFinanceMember === true;
    if (d.id === "audit-console") return isSuperuser(ctx.userId);
    if (d.id === "spb-analytics") return ctx.hideSpbAnalytics !== true;
    return true;
  });
}
